import {
  Colors,
  extendTheme,
  ThemeComponents,
  ThemeConfig,
  withDefaultColorScheme,
  withDefaultSize,
} from "@chakra-ui/react";

const fonts = {
  heading: "Manrope",
  body: "Manrope",
};
const colors: Colors = {
  white: "#FFFFFF",
  lightblue: "#4653ED0D", // ok
  blue: "#4653ED", // ok
  darkblue: "#0B0D2B", // ok
  // bg: "#ECEFF6", // figma
  bg: "#e8d2eb", // ok
  navy: "#353FB5", // ok
  border: "#0B0D2B",
  red: "#FF383E", // ok
  green: "#7BC429", // ok
  lightgreen: "#F9FFEA", // ok
  neutral: {
    gray00: "#FFFFFF", // ok
    gray100: "#F4F4F4", // ok
    gray200: "#CDCDCD", // ok
    gray300: "#9F9F9F", // ok
    gray400: "#717171", // ok
    gray500: "#505052", // ok
    gray600: "#3F3F45", // ok
    gray700: "#343439", // ok
    gray800: "#242428", // ok
    gray900: "#16161A", // ok
  },

  // Base color: #2C4142. Generated by https://smart-swatch.netlify.app
  main: {
    50: "#E6FFFA",
    100: "#B2F5EA",
    200: "#81E6D9",
    300: "#4FD1C5",
    400: "#38B2AC",
    500: "#319795",
    600: "#3536E8", // revamp ui, do not change this
    700: "#285E61",
    800: "#234E52",
    900: "#1D4044",
    primary: "#353FB5",
    secondary: "#7297A7",
    text: "#000000",
  },
  black: {
    100: "#575757",
    400: "#305853",
  },
  link: {
    active: "#975ca5",
  },
  secondary: {
    50: "#daffff",
    100: "#afffff",
    200: "#80ffff",
    300: "#52ffff",
    400: "#31fefe",
    500: "#24e4e5",
    600: "#12b2b3",
    700: "#007f80",
    800: "#004c4e",
    900: "#001b1c",
  },
  baliHai: {
    500: "#84A7B6",
  },
};

const fontSizes = {
  xs: "0.625rem",
  sm: "0.75rem",
  md: "0.875rem", // 14px, fixed
  lg: "1rem", // 16px, fixed
  xl: "1.125rem",
  "2xl": "1.5rem", // 24px, fixed
  "3xl": "1.75rem",
  "4xl": "2.125rem",
  "5xl": "2.875rem",
  "6xl": "3.625rem",
  "7xl": "4.375rem",
  "8xl": "5.875rem",
  "9xl": "7.875rem",
};

const borderRadius = {
  radii: {
    none: "0",
    sm: "0.125rem",
    base: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem", // ok
    xl: "0.75rem", // ok
    "2xl": "1rem",
    "3xl": "1.25rem", // ok
    full: "9999px",
  },
};

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const shadows = {
  outline: "0 0 0 3px #49696b",
};

const components: ThemeComponents = {
  Button: {
    baseStyle: {
      borderRadius: "2.5rem",
      px: 4,
    },
    variants: {
      solid: {
        px: 4,
        background: "navy",
        color: "white",
      },
      unstyled: {
        p: 0,
        m: 0,
        borderRadius: 0,
      },
      outline: {
        px: 4,
      },
      link: {
        color: "navy",
      },
    },
    sizes: {
      sm: {
        minW: "40px",
        minH: "40px",
        fontWeight: "700",
        fontSize: "16px",
      },
      md: {
        minW: "40px",
        minH: "40px",
        h: "47px",
        fontWeight: "700",
        fontSize: "16px",
      },
    },
  },
  Link: {
    baseStyle: {
      textDecoration: "none",
      color: "navy",
    },
  },
  FormLabel: {
    baseStyle: {
      fontSize: "md",
    },
  },
  Text: {
    baseStyle: {
      color: "#000000",
    },
  },
  Input: {
    defaultProps: {
      variant: "outline",
    },
    baseStyle: {
      field: {
        color: "neutral.gray900", // change the input text color, ok
      },
      element: {
        width: 12,
        height: 12,
      },
    },
    variants: {
      outline: {
        field: {
          backgroundColor: "#FFFFFF",
          borderWidth: "1px",
          minH: "3rem",
          borderRadius: "0.75rem",
          color: "neutral.gray900",
          borderColor: "neutral.gray100", // ok
          _hover: {
            borderColor: "neutral.gray100", // ok
          },
          _focus: {
            borderColor: "neutral.gray100", // ok
          },
          _focusVisible: {
            backgroundColor: "#FAFAFA",
            boxShadow: "none",
          },
        },
      },
    },
  },
  Textarea: {
    baseStyle: {
      color: "teal.500", // change the input text color
    },
    variants: {
      outline: {
        border: "1px solid",
        borderColor: "teal.500",
        _hover: {
          borderColor: "teal.600",
        },
      },
    },
  },
};

// Global theme
const styles = {
  global: {
    // styles for the `body`
    body: {
      bg: "#ECEFF6",
    },
    "*::placeholder": {
      color: "neutral.gray300",
    },
  },
};

export const uiTheme = extendTheme(
  { styles, colors, config, components, shadows, fonts, fontSizes, borderRadius },
  withDefaultColorScheme({ colorScheme: "main" }),
  withDefaultSize({ size: "md" })
);
